// src/pages/events/EventAttendeesDialog.js
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Typography,
  CircularProgress,
  Box,
  Divider,
  IconButton,
  Tooltip,
  Paper
} from '@mui/material';
import {
  People as PeopleIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../../firebase';

const EventAttendeesDialog = ({ open, onClose, eventId, eventTitle }) => {
  const [attendees, setAttendees] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && eventId) {
      fetchAttendees();
    }
  }, [open, eventId]);

  const fetchAttendees = async () => {
    setLoading(true);
    try {
      // Obtener el evento para conseguir la lista de asistentes
      const eventDoc = await getDoc(doc(db, 'events', eventId));
      if (eventDoc.exists()) {
        const eventData = eventDoc.data();
        const attendeeIds = eventData.asistentes || [];
        
        // Obtener información de cada asistente
        const attendeePromises = attendeeIds.map(async (userId) => {
          try {
            const userDoc = await getDoc(doc(db, 'users', userId));
            if (userDoc.exists()) {
              const userData = userDoc.data();
              return {
                id: userId,
                name: `${userData.firstName || ''} ${userData.lastName || ''}`.trim(),
                email: userData.email || '',
                phone: userData.phone || '',
                firstName: userData.firstName || '',
                lastName: userData.lastName || ''
              };
            }
          } catch (error) {
            console.error('Error loading attendee:', error);
          }
          return null;
        });
        
        const attendeeResults = await Promise.all(attendeePromises);
        setAttendees(attendeeResults.filter(Boolean));
      }
    } catch (error) {
      console.error('Error fetching attendees:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyEmails = () => {
    const emails = attendees
      .filter(attendee => attendee.email)
      .map(attendee => attendee.email)
      .join(', ');
    
    navigator.clipboard.writeText(emails).then(() => {
      // Podrías mostrar un snackbar aquí
      console.log('Emails copiados al portapapeles');
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            Asistentes - {eventTitle}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" py={4}>
            <CircularProgress />
          </Box>
        ) : attendees.length === 0 ? (
          <Box textAlign="center" py={4}>
            <PeopleIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              No hay asistentes confirmados
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Los usuarios podrán confirmar su asistencia desde la app móvil.
            </Typography>
          </Box>
        ) : (
          <>
            <Paper sx={{ p: 2, mb: 2, bgcolor: 'primary.light', color: 'primary.contrastText' }}>
              <Typography variant="h6">
                Total de asistentes: {attendees.length}
              </Typography>
              {attendees.filter(a => a.email).length > 0 && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleCopyEmails}
                  sx={{ mt: 1, borderColor: 'primary.contrastText', color: 'primary.contrastText' }}
                >
                  Copiar emails ({attendees.filter(a => a.email).length})
                </Button>
              )}
            </Paper>
            
            <List>
              {attendees.map((attendee, index) => (
                <React.Fragment key={attendee.id}>
                  <ListItem>
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: 'primary.main' }}>
                        {attendee.firstName?.charAt(0) || attendee.name?.charAt(0) || 'U'}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Typography variant="subtitle1">
                          {attendee.name || 'Sin nombre'}
                        </Typography>
                      }
                      secondary={
                        <Box>
                          {attendee.email && (
                            <Box display="flex" alignItems="center" mt={0.5}>
                              <EmailIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                              <Typography variant="body2">
                                {attendee.email}
                              </Typography>
                            </Box>
                          )}
                          {attendee.phone && (
                            <Box display="flex" alignItems="center" mt={0.5}>
                              <PhoneIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                              <Typography variant="body2">
                                {attendee.phone}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      }
                    />
                    <Box>
                      {attendee.email && (
                        <Tooltip title="Enviar email">
                          <IconButton
                            size="small"
                            onClick={() => window.open(`mailto:${attendee.email}`)}
                          >
                            <EmailIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      {attendee.phone && (
                        <Tooltip title="Llamar">
                          <IconButton
                            size="small"
                            onClick={() => window.open(`tel:${attendee.phone}`)}
                          >
                            <PhoneIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </ListItem>
                  {index < attendees.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          </>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose}>
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EventAttendeesDialog;